import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ORDENABLE_PAGE_SIZE, REQUEST_STALE_TIME_IN_MS } from '@/constants';
import {
  createLessonBlock,
  deleteLessonBlock,
  LessonBlockOrder,
  updateLessonBlocksOrder,
} from '@/data/services/lessonBlocksServices';
import { lessonBlocksQueryKeys } from '@/data/services/querykeys';
import Lesson from '@/models/Lesson';
import { PickLessonBlockInfo } from '@/models/_deprecated/LessonBlock';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import { CacheType } from '@/data/hook/query/v2/strategy/CacheStrategyFactory';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { mountKey } from '@/utils/courses';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';

type UseCourseUnitsProps = {
  slug: string;
};

const useCourseUnits = ({ slug }: UseCourseUnitsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.courseWorkspace.unitsTab',
  });

  const { tree } = useTreeCourses();

  const queryClient = useQueryClient();

  const {
    results: units,
    isLoading,
    cacheManager,
    ...infinityProps
  } = useInfiniteService({
    ...lessonBlocksQueryKeys.list(slug, { pageSize: ORDENABLE_PAGE_SIZE })._ctx
      .infinity,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: (data: PickLessonBlockInfo) => createLessonBlock(slug, data),
    onSuccess: unit => {
      const parentKey = mountKey({ type: 'course', id: slug });
      cacheManager.add(unit, { position: 'end' });
      tree?.current.upsert(
        {
          id: mountKey({ type: 'unit', id: unit.id }),
          name: unit.name,
          type: 'unit',
          realId: unit.id,
        },
        parentKey,
      );
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: (unitId: number) => deleteLessonBlock(slug, unitId),
    onSuccess: (_, unitId) => {
      const newUnits = units.filter(unit => unit.id !== unitId);
      cacheManager.remove(unitId);
      cacheManager.set(
        reorder(
          units,
          newUnits.map(unit => unit.id),
        ),
      );
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });
  const reorderMutation = useMutation({
    onMutate: order => {
      const lessonManagers = updateLessonsCache(order);
      cacheManager.set(
        reorder(
          units,
          order.map(unit => unit.id),
        ),
      );
      return { lessonManagers };
    },
    mutationFn: (order: LessonBlockOrder[]) =>
      updateLessonBlocksOrder(slug, order),
    onSuccess: () => {
      alert.success(t('reorderSuccess'));
    },
    onError: (err, _order, context) => {
      context?.lessonManagers.forEach(manager => manager.undo());
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  const createLessonCache = (order: LessonBlockOrder[]) => {
    const managers = new Map<number, ListCacheManager<Lesson>>();
    order.forEach(unit => {
      const manager = new ListCacheManager<Lesson>({
        queryKey: lessonBlocksQueryKeys.listLessons(slug, unit.id, {
          pageSize: ORDENABLE_PAGE_SIZE,
        }).queryKey,
        cacheType: CacheType.LIST,
        queryClient,
      });
      managers.set(unit.id, manager);
    });
    return managers;
  };

  const updateLessonsCache = (order: LessonBlockOrder[]) => {
    const managers = createLessonCache(order);

    const lessons = order.flatMap(unit => {
      const lessonCache = managers.get(unit.id);
      return lessonCache?.get() ?? [];
    });

    order.forEach(unit => {
      const lessonCache = managers.get(unit.id);
      const reorderedLessons = reorder(
        lessons,
        unit.lessonsIds.map(lesson => lesson.id),
      );
      lessonCache?.set(reorderedLessons);
    });

    return managers;
  };

  return {
    units,
    isLoading,
    createUnit: createMutation.mutate,
    isCreating: createMutation.isPending,
    deleteUnit: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
    reorderUnits: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
    ...infinityProps,
  };
};

export default useCourseUnits;
